import { Link, router } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PortalAppointment } from '@/types'

type Props = {
  appointment: PortalAppointment
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function handleCancel(id: string) {
  if (!confirm('Are you sure you want to cancel this appointment? This cannot be undone.')) return
  router.post(`/notary_portal/appointments/${id}/cancel`)
}

export default function AppointmentShow({ appointment }: Props) {
  return (
    <NotaryPortalLayout
      title="Appointment"
      breadcrumbs={[
        { label: 'Notary Portal', href: '/notary_portal/dashboard' },
        { label: 'Appointments', href: '/notary_portal/appointments' },
        { label: appointment.business_name },
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{appointment.business_name}</h1>
            <p className="text-sm text-muted-foreground">{appointment.account_name}</p>
          </div>
          {appointment.cancelled ? (
            <Badge className="bg-red-100 text-red-700 border border-red-200/60 text-[11px] font-normal">
              Cancelled
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[11px] font-normal">Scheduled</Badge>
          )}
        </div>

        <DetailGrid
          columns={2}
          items={[
            { label: 'Date & Time', value: formatDateTime(appointment.scheduled_at) },
            { label: 'Duration', value: `${appointment.duration_minutes} minutes` },
            { label: 'Session Mode', value: (
              <Badge variant="outline" className="text-[11px] capitalize font-normal">
                {appointment.session_mode.replace(/_/g, ' ')}
              </Badge>
            ) },
            { label: 'Timezone', value: appointment.timezone },
            { label: 'Documents', value: `${appointment.documents_count} document${appointment.documents_count !== 1 ? 's' : ''}` },
          ]}
        />

        <div className="flex gap-2 pt-4">
          <Button size="sm" asChild className="h-8 text-[13px]">
            <Link href={`/notary_portal/notarizations/${appointment.notarization_id}`}>
              View Notarization
            </Link>
          </Button>
          {!appointment.cancelled && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleCancel(appointment.id)}
              className="h-8 text-[13px] text-destructive hover:text-destructive"
            >
              Cancel Appointment
            </Button>
          )}
        </div>
      </div>
    </NotaryPortalLayout>
  )
}
